import React, { useState, useCallback } from "react";
import { ServiceJourneyStep } from "api-client";
import Tabs, { Tab } from "components/Nav/Tabs";
import Button from "components/Buttons/Button";
import MarkdownContent from "components/Markdown";
import classNames from "classnames";
import { onOpenContentItem } from "utils/route";
import "./ServiceJourneyContainer.scss";
import useBreakPoint from "hooks/useBreakPoint";
import { Breakpoint } from "types/breakpoint";
import Modal from "components/Modals/Modal";
import CloseButton from "components/Buttons/CloseButton";
import CollapseButton from "components/Buttons/CollapseButton";

interface NextStepDotProps {
    isLast?: boolean;
}

const NextStepDot = ({ isLast }: NextStepDotProps) => {
    if (!isLast) {
        return <div className="next-step-dot" />;
    }
    return null;
};

interface ServiceJourneyLinkButtonProps {
    id?: number;
    step: ServiceJourneyStep;
}

const ServiceJourneyLinkButton = ({ step, id }: ServiceJourneyLinkButtonProps) => {
    const compassLinks = step.serviceLinks?.filter((link) => link.link.includes("sharepoint"));
    const myHRLinks = step.serviceLinks?.filter((link) => link.link.includes("my.hr"));
    return (
        <>
            <div className="knowledge-article-links">
                <h4>Knowledge Articles</h4>
                {step.articles?.map((article) => {
                    return (
                        <Button
                            key={article.id}
                            className="tab-section__right__button"
                            text={article.title}
                            onClick={() => onOpenContentItem(article.id)}
                            size="medium"
                            variant="secondary"
                        />
                    );
                })}
                {myHRLinks?.map((link) => {
                    return (
                        <Button
                            key={id}
                            className="tab-section__right__button"
                            text={link.title}
                            to={link.link}
                            size="medium"
                            variant="secondary"
                        />
                    );
                })}
            </div>
            {compassLinks && compassLinks.length > 0 && (
                <div className="compass-links">
                    <h4>Compass</h4>
                    {compassLinks.map((link) => {
                        return (
                            <Button
                                key={id}
                                className="tab-section__right__button"
                                text={link.title}
                                to={link.link}
                                size="medium"
                                variant="secondary"
                            />
                        );
                    })}
                </div>
            )}
        </>
    );
};

interface JourneyStepsProps {
    id?: number;
    journeySteps: ServiceJourneyStep[];
    className?: string;
    isRecruitment?: boolean;
}

const ServiceJourneyContainer = ({ journeySteps, className, id, isRecruitment }: JourneyStepsProps) => {
    const [selectedStep, setSelectedStep] = useState("");
    const handleTabChange = useCallback((_: React.MouseEvent<HTMLButtonElement>, tab: string) => {
        setSelectedStep(tab);
    }, []);
    const isUntilLargeDesktop = useBreakPoint(Breakpoint.untilLargeDesktop);
    const [isModalOpen, setIsModalOpen] = useState(false);

    if (isUntilLargeDesktop) {
        return (
            <div className="service-journey-mobile">
                {journeySteps.map((step, index) => {
                    return (
                        <>
                            <Button
                                key={id}
                                className="service-journey-mobile__step-button"
                                text={`${index + 1}. ` + step.title}
                                onClick={() => {
                                    setIsModalOpen(true);
                                    setSelectedStep(step.name);
                                }}
                                size="medium"
                                variant="secondary"
                            />
                            {selectedStep == step.name && isModalOpen && (
                                <Modal
                                    isVisible={isModalOpen}
                                    onClickBackdrop={() => setIsModalOpen(false)}
                                    classes={{ container: "service-journey-mobile__modal" }}
                                >
                                    <div className="service-journey-mobile__modal__header">
                                        <h3 className="service-journey-mobile__modal__header__title">
                                            {`${index + 1}. ` + step.name}
                                        </h3>
                                        <CloseButton
                                            handleClose={() => {
                                                setIsModalOpen(false);
                                            }}
                                            className="service-journey-mobile__modal__header__close-button"
                                        />
                                    </div>
                                    <div className="service-journey-mobile__modal__body" key={id}>
                                        <div className="service-journey-mobile__modal__body__description">
                                            <MarkdownContent
                                                content={step.description}
                                                className="tab-section__left__description"
                                            />
                                        </div>
                                        <div className="service-journey-mobile__modal__body__buttons">
                                            <ServiceJourneyLinkButton step={step} />
                                        </div>
                                    </div>
                                </Modal>
                            )}
                        </>
                    );
                })}
            </div>
        );
    } else {
        return (
            <div className={classNames("service-journey", className)}>
                <Tabs className="tabs" selectedTab={selectedStep} handleChange={handleTabChange}>
                    {journeySteps.map((step, index) => {
                        return (
                            <Tab classes={{ root: "tab" }} key={id ?? index} label={step.name}>
                                <div className="tab__item">
                                    <div className="tab__item__header">
                                        <div className={classNames("tab__item__icon", { "tab__item__icon--recruitment": isRecruitment })} data-step={index + 1} />
                                        <div className="tab__item__name">{step.name}</div>
                                    </div>
                                    <span className="tab__item__small-dot">
                                        <NextStepDot isLast={index == journeySteps.length - 1} />
                                    </span>
                                </div>
                            </Tab>
                        );
                    })}
                </Tabs>
                {journeySteps.map((step) => {
                    if (selectedStep === step.name) {
                        return (
                            <div className="tab-section" key={id}>
                                <div className="tab-section__left">
                                    <h3 className="tab-section__left__name">{step.name}</h3>
                                    <MarkdownContent
                                        content={step.description}
                                        className="tab-section__left__description"
                                    />
                                </div>
                                <div className="tab-section__vertical-line" />
                                <div className="tab-section__right">
                                    <ServiceJourneyLinkButton step={step} />
                                </div>
                            </div>
                        );
                    } else {
                        return null;
                    }
                })}
                {selectedStep !== "" && (
                    <CollapseButton
                        onClick={() => {
                            setSelectedStep("");
                        }}
                    />
                )}
            </div>
        );
    }
};

export default ServiceJourneyContainer;
